import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import RecommendationBand from '@v2/shared/components/RecommendationBand';
import SortSelect from '@v2/shared/components/SortSelect';
import CategoryDescription from './CategoryDescription';
import CategorySearch from './CategorySearch';
import FilterSidebar from './FilterSidebar';
import NoResults from './NoResults';
import Pagination from './Pagination';
import ProductGrid from './ProductGrid';
import { buildFilterHref, carryListingState } from './filterUrl';
import { CategoryFacetValue, CategoryProps } from './types';

/**
 * The category listing — `/categories/education` and everything shaped like it,
 * `/bestsellers` included — and the search results page, which is the same listing with a
 * query in place of a section (`search.html`, through `CategoryPageProps::buildSearch()`).
 *
 * Still served by the legacy backend — `section.php`, or `search.php` for a search: the
 * search, the filters, the sort and the paging all happen before this component exists,
 * and `section.html` or `search.html` hands the result over as props through
 * `CategoryPageProps`. That split is deliberate (Asana 1218587601752610 and 1218588620115538
 * are the UI halves; 1218588213970004 and 1218587602171494 move the backends), so the
 * listing fetches nothing and every control is a navigation, exactly as it was. The only
 * fetch on the page is the search's recommendation bands, which ask for their own products
 * once the page is up, as the legacy bands did.
 *
 * The page is heavily crawled, so three things are load-bearing rather than incidental:
 * the heading is the only `h1`, every filter link keeps the href the legacy sidebar gave
 * it (see `filterUrl.ts`), and pagination stays real urls (see `Pagination.tsx`).
 *
 * The grid is placed explicitly from `lg` up rather than relying on source order, so the
 * rail can sit beside the whole column while the phone still reads heading → filters →
 * results, which is the order the legacy page had.
 */
export default function Category({
  heading,
  description,
  breadcrumbs,
  totalProducts,
  startOffset,
  listName,
  sort,
  facets,
  selectedFilterCount,
  products,
  pagination,
  search,
  showMsrp = true,
  showResultCount = true,
  noResults = false,
  recommendations = [],
}: CategoryProps) {
  if (noResults) {
    return (
      <>
        <Breadcrumbs items={breadcrumbs} />
        <NoResults heading={heading} />
      </>
    );
  }

  const go = (href: string) => {
    window.location.href = href;
  };

  // A value with a url of its own navigates to it; one without folds its parameter into
  // the query string instead. Both keep whatever else was already there — see
  // `filterUrl.ts`, which is the legacy `filter` js-module restated.
  const onFilter = (value: CategoryFacetValue, parameter: string) =>
    go(
      buildFilterHref(
        value.href,
        value.filterValue === null ? null : parameter,
        value.filterValue,
        window.location.search
      )
    );

  const onSearch = (query: string) => {
    const params = withSearch(window.location.search, query);

    go(params ? `${window.location.pathname}?${params}` : window.location.pathname);
  };

  // Two instances, one per breakpoint, so each needs an id of its own: `htmlFor` binds
  // a label to the *first* element with that id, so a duplicate silently points both
  // labels at the hidden one.
  const sortControl = (id: string) => (
    <SortSelect
      id={id}
      label="Sort by"
      options={sort.options}
      value={sort.value}
      onChange={(href) => go(carryListingState(href, window.location.search, 'xSort'))}
    />
  );

  return (
    <>
      <Breadcrumbs items={breadcrumbs} />

      {/*
         * Explicit rows, not just columns. The rail spans both, and with auto rows a
         * browser distributes a spanning item's height across the rows it covers — so a
         * long Country list stretched the header row and pushed the grid a screen and a
         * half down the page. `auto 1fr` pins the header to its own height and gives the
         * rest to the products.
         */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[248px_1fr] lg:grid-rows-[auto_1fr] lg:gap-9">
        <header className="lg:col-start-2 lg:row-start-1">
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="m-0 text-2xl font-bold text-slate-900">{heading}</h1>
            <div className="hidden lg:block">{sortControl('section-sort-by')}</div>
          </div>

          <CategoryDescription description={description} />
        </header>

        <div className="lg:col-start-1 lg:row-start-1 lg:row-span-2">
          {showResultCount && (
            <div className="mb-3 flex items-center justify-between gap-3 lg:hidden">
              <span className="text-sm text-slate-600">
                <span className="font-bold text-slate-900">{totalProducts.toLocaleString('en-US')}</span> results
              </span>
            </div>
          )}

          <FilterSidebar
            facets={facets}
            selectedCount={selectedFilterCount}
            onNavigate={onFilter}
            mobileControls={sortControl('section-sort-by-mobile')}
          />
        </div>

        <div className="lg:col-start-2 lg:row-start-2">
          {search && <CategorySearch search={search} onSubmit={onSearch} />}

          <ProductGrid products={products} list={listName} startOffset={startOffset} showMsrp={showMsrp} />

          <Pagination pagination={pagination} />
        </div>
      </div>

      {/*
         * Full width under the rail and the results, where the legacy search page put its
         * bands. Each draws itself only once its products arrive, so the box collapses when
         * none of them do.
         */}
      {recommendations.length > 0 && (
        <div className="mt-10 empty:hidden md:mt-14">
          {recommendations.map((band) => (
            <RecommendationBand key={`${band.type}:${band.product ?? band.category}`} {...band} showMsrp={showMsrp} />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * `xSearch` goes in the query string beside the filters, and turning a page with a
 * different search in hand is meaningless, so `xPage` goes — both the same rules the
 * legacy `filter` module applied to its own search box.
 */
function withSearch(search: string, query: string): string {
  const params = new URLSearchParams(search);

  params.delete('xPage');

  if (query === '') {
    params.delete('xSearch');
  } else {
    params.set('xSearch', query);
  }

  return params.toString();
}

Category.layoutName = 'Layouts.Full';
